import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuVoteHeader } from '../../../widgets/menu-vote-header';
import { ResultBanner } from '../../../widgets/result-banner';
import { ResultPromoBanner } from '../../../widgets/result-promo-banner';
import { ResultRankingList, ResultRankingScrim } from '../../../widgets/result-ranking';
import { RestaurantCard } from '../../../entities/restaurant-card';
import { MenuCard } from '../../../entities/menu-card';
import { RewardCta } from '../../../features/reward-cta';
import { WinnerConfetti } from '../../../features/winner-confetti';
import type { MenuItem } from '../../../shared/config/menuVoteData';
import { findMenuItemById, VOTE_SELECTED_MENU_STORAGE_KEY } from '../../../shared/config/menuVoteData';
import {
  RESULT_DECORATIONS,
  RESULT_RESTAURANT,
  RESULT_TITLE,
  REWARD_CTA,
  WINNER,
} from '../../../shared/config/menuResultData';
import { buildRankingForWinner } from '../lib/buildRankingForWinner';
import { useRankingReveal } from '../lib/useRankingReveal';
import { useRestaurantScrollVisibility } from '../lib/useRestaurantScrollVisibility';
import {
  BannerWrap,
  CtaWrap,
  FixedOverlay,
  Footer,
  HeaderWrap,
  Main,
  PageRoot,
  PromoBanner,
  Restaurant,
  RevealWinnerStats,
  ScrollBody,
  ScrollBottomSpacer,
  ScrollCanvas,
  Winner,
  WinnerCard,
} from './MenuVoteResultPage.styles';

const PAGE_TITLE = '투표 결과 | 리워드';
const PAGE_DESCRIPTION =
  '내가 선택한 메뉴가 1위! 게이지 리워드를 획득하세요. 메뉴 투표 결과와 순위를 확인할 수 있습니다.';
const OG_IMAGE = `${process.env.PUBLIC_URL ?? ''}/assets/menu-result/trophy.png`;
const ANIMATE_KEY = 'menu-vote-animate-ranking';

interface ResultLocationState {
  animateRanking?: boolean;
  selectedMenu?: MenuItem;
}

function toWinnerMenuItem(menu: MenuItem): MenuItem {
  return {
    id: menu.id,
    name: menu.name,
    image: menu.image,
    imageSize: menu.imageSize,
    isAnswer: true,
    isPlaceholder: false,
  };
}

function resolveVotedMenuItem(state: ResultLocationState | null): MenuItem {
  const fromState = state?.selectedMenu;
  if (fromState && !fromState.isPlaceholder) {
    return toWinnerMenuItem(fromState);
  }

  const storedId = sessionStorage.getItem(VOTE_SELECTED_MENU_STORAGE_KEY);
  const fromStorage = storedId ? findMenuItemById(storedId) : undefined;
  if (fromStorage && !fromStorage.isPlaceholder) {
    return toWinnerMenuItem(fromStorage);
  }

  return {
    id: WINNER.id,
    name: WINNER.name,
    image: WINNER.image,
    imageSize: WINNER.imageSize,
    isAnswer: WINNER.isAnswer,
    isPlaceholder: false,
  };
}

function useResultDocumentMeta() {
  useEffect(() => {
    document.title = PAGE_TITLE;
    const upsertMeta = (
      selector: string,
      attr: 'name' | 'property',
      key: string,
      content: string,
    ) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    upsertMeta('meta[name="description"]', 'name', 'description', PAGE_DESCRIPTION);
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', PAGE_TITLE);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', PAGE_DESCRIPTION);
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', OG_IMAGE);
    document.documentElement.lang = 'ko';
  }, []);
}

export interface MenuVoteResultPageProps {
  onBack?: () => void;
}

export function MenuVoteResultPage({ onBack }: MenuVoteResultPageProps) {
  useResultDocumentMeta();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as ResultLocationState | null;
  const winnerItem = resolveVotedMenuItem(locationState);
  const rankingItems = useMemo(
    () => buildRankingForWinner(winnerItem),
    [winnerItem.id, winnerItem.name],
  );
  const stateAnimate = locationState?.animateRanking === true;
  const storageAnimate = sessionStorage.getItem(ANIMATE_KEY) === '1';
  const animateRanking = stateAnimate || storageAnimate;

  useEffect(() => {
    if (storageAnimate) {
      sessionStorage.removeItem(ANIMATE_KEY);
    }
    sessionStorage.removeItem(VOTE_SELECTED_MENU_STORAGE_KEY);
  }, [storageAnimate]);

  const { visibleRanks, showScrim, showWinner, showCta, showRestaurant } =
    useRankingReveal(animateRanking);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  const showRestaurantByScroll = useRestaurantScrollVisibility(scrollBodyRef, showRestaurant);
  const restaurantVisible = showRestaurant && showRestaurantByScroll;
  const promoVisible = showRestaurant && !showRestaurantByScroll;
  const winnerCardRef = useRef<HTMLDivElement>(null);
  const handleBack = onBack ?? (() => navigate('/'));

  return (
    <PageRoot>
      <ResultRankingScrim showScrim={showScrim} />

      <ScrollBody ref={scrollBodyRef}>
        <ScrollCanvas>
          <Main>
            <BannerWrap $visible={showWinner} aria-hidden={!showWinner}>
              <ResultBanner
                trophy={RESULT_DECORATIONS.trophy}
                coin={RESULT_DECORATIONS.coin}
                titleLine1={RESULT_TITLE.line1}
                titleLine2={RESULT_TITLE.line2}
                animateTitle={animateRanking}
                titlePlaying={showWinner}
              />
            </BannerWrap>

            <Winner aria-label="우승 메뉴" aria-hidden={!showWinner}>
              <WinnerCard ref={winnerCardRef} $visible={showWinner}>
                <MenuCard item={winnerItem} active />
              </WinnerCard>
              <RevealWinnerStats
                $visible={showWinner}
                ratePercent={WINNER.ratePercent}
                voteCount={WINNER.voteCount}
              />
            </Winner>

            <ResultRankingList items={rankingItems} visibleRanks={visibleRanks} />
          </Main>

          <ScrollBottomSpacer aria-hidden="true" />
        </ScrollCanvas>
      </ScrollBody>

      <WinnerConfetti active={showWinner} originRef={winnerCardRef} />

      <FixedOverlay>
        <HeaderWrap>
          <MenuVoteHeader variant="white" onBack={handleBack} />
        </HeaderWrap>

        <Footer>
          <Restaurant
            $visible={restaurantVisible}
            aria-label="식당 정보"
            aria-hidden={!restaurantVisible}
          >
            <RestaurantCard
              name={RESULT_RESTAURANT.name}
              distance={RESULT_RESTAURANT.distance}
              description={RESULT_RESTAURANT.description}
              logo={RESULT_RESTAURANT.logo}
              logoSize={RESULT_RESTAURANT.logoSize}
            />
          </Restaurant>

          <PromoBanner
            $visible={promoVisible}
            aria-label="프로모션 배너"
            aria-hidden={!promoVisible}
          >
            <ResultPromoBanner />
          </PromoBanner>

          <CtaWrap $visible={showCta} aria-hidden={!showCta}>
            <RewardCta
              pointLabel={REWARD_CTA.pointLabel}
              pointValue={REWARD_CTA.pointValue}
              message={REWARD_CTA.message}
              animatePointValue={animateRanking && showCta}
            />
          </CtaWrap>
        </Footer>
      </FixedOverlay>
    </PageRoot>
  );
}
